import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { api } from '../lib/api';
import { useI18n, type TranslationKey } from '../lib/i18n';
import { FormPage } from '../components/FormPage';
import { Field, Select, Textarea, useToast } from '../components/ui';
import { clientName, crStatusLabel, type ChangeRequestDetail, type ChangeRequestStatus } from './ClientCrs';
import { TaskFormPage } from './TaskForm';

/**
 * Route: `/client-crs/:id/convert` — an approved change request becomes a task:
 * the New task form, started from the request's title, details and files, for a
 * person or a workflow, with a start and a deadline.
 */
export function ClientCrConvertPage() {
  const { id = '' } = useParams();
  return <TaskFormPage changeRequestId={id} />;
}

/**
 * Route: `/client-crs/:id/status?to=APPROVED` — moving a client's change request
 * on, with the note the client reads beside the new status. A page, like every
 * form in the admin; rejecting needs the note.
 */
export function ClientCrStatusPage() {
  const { id = '' } = useParams();
  const [params] = useSearchParams();
  const { t, locale } = useI18n();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const existing = useQuery({
    queryKey: ['client-cr', id],
    queryFn: () => api.get<ChangeRequestDetail>(`/client-change-requests/${id}`),
  });

  const requested = params.get('to') as ChangeRequestStatus | null;
  const options = existing.data?.nextStatuses ?? [];
  const [to, setTo] = useState<ChangeRequestStatus | ''>('');
  const target: ChangeRequestStatus | '' = to || (requested && options.includes(requested) ? requested : options[0] ?? '');
  const [note, setNote] = useState('');
  const noteRequired = target === 'REJECTED';

  const save = useMutation({
    mutationFn: () =>
      api.post(`/client-change-requests/${id}/status`, { status: target, note: note.trim() || undefined }),
    onSuccess: () => {
      toast.success(t('crs.updated'));
      void queryClient.invalidateQueries({ queryKey: ['client-cr', id] });
      void queryClient.invalidateQueries({ queryKey: ['client-crs'] });
      navigate(`/client-crs/${id}`);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const request = existing.data;
  // Done is a person's call, never the work's (the operator's choice) — so say when the work is still going.
  const openWork = (request?.tasks ?? []).filter((task) => task.status !== 'DONE' && task.status !== 'CANCELLED');

  return (
    <FormPage
      title={request ? `${request.number} — ${t('crs.changeStatus')}` : t('crs.changeStatus')}
      subtitle={request ? `${request.title} · ${clientName(request.client, locale)}` : undefined}
      backTo={`/client-crs/${id}`}
      backLabel={request?.number ?? t('nav.clientCrs')}
      submitLabel={target ? t(`crs.action.${target}` as TranslationKey) : t('common.save')}
      destructive={target === 'REJECTED'}
      submitting={save.isPending}
      disabled={!target || (noteRequired && !note.trim())}
      loading={existing.isLoading}
      error={existing.error}
      onRetry={() => void existing.refetch()}
      onSubmit={() => save.mutate()}
    >
      <div className="space-y-4">
        <Field label={t('crs.moveTo')} required>
          <Select value={target} onChange={(e) => setTo(e.target.value as ChangeRequestStatus)}>
            {options.map((status) => (
              <option key={status} value={status}>
                {crStatusLabel(t, status)}
              </option>
            ))}
          </Select>
        </Field>
        <Field
          label={t('crs.reviewNote')}
          required={noteRequired}
          hint={noteRequired ? t('crs.reviewNoteRequired') : t('crs.reviewNoteHint')}
        >
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} className="min-h-[120px]" autoFocus />
        </Field>
        {target === 'DONE' && openWork.length > 0 && (
          <p className="rounded-md border border-warning/35 bg-warning/8 px-3 py-2 text-body-sm text-warning-fg dark:text-warning-dark">
            {t('crs.workNotFinished')}
          </p>
        )}
      </div>
    </FormPage>
  );
}
